import {
    Component,
    inject,
    input,
    AfterViewInit,
    ViewContainerRef,
    viewChild,
} from '@angular/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
    CategoriesComponent,
    Category,
} from './categories/categories.component';
import { ContactHofComponent } from '../contact-hof/contact-hof.component';
import { DOMHandlerService } from '../../../services/domhandler.service';
import {
    Bestsellers,
    BestsellersComponent,
} from '../bestsellers/bestsellers.component';
import { RouterLink } from '@angular/router';
import { LanguageService } from '../../../services/language.service';
import { TranslatePipe } from '@ngx-translate/core';
import {
    FullImage,
    FullImageComponent,
} from '../../common/full-image/full-image.component';
import { BlockNode } from '../../../types/rich-text.types';
import { UmamiService } from '../../../services/umami.service';
import SplitType from 'split-type';
import { TypewriterService } from '../../../services/typewriter.service';
import { Image } from '../../apartment/header/header.component';
import { NgClass } from '@angular/common';
import { GridSectionComponent } from './grid-section/grid-section.component';
import { ThreeLogoComponent } from '../three-logo/three-logo.component';

gsap.registerPlugin(ScrollTrigger);

@Component({
    selector: 'app-hof-page',
    imports: [
        TranslatePipe,
        NgClass,
        CategoriesComponent,
        ContactHofComponent,
        BestsellersComponent,
        RouterLink,
        FullImageComponent,
        GridSectionComponent,
    ],
    host: { class: 'block overflow-hidden relative font-futura !mt-0' },
    templateUrl: './hof-page.component.html',
})
export class HofPageComponent implements AfterViewInit {
    data = input.required<HofPage>();
    domHandler = inject(DOMHandlerService);
    languageService = inject(LanguageService);
    umami = inject(UmamiService);
    typewriterService = inject(TypewriterService);
    private viewContainerRef = viewChild('logoRef', {
        read: ViewContainerRef,
    });

    ngOnInit(): void {
        import('../three-logo/three-logo.component')
            .then((m) => m.ThreeLogoComponent)
            .then((component) => {
                this.viewContainerRef()?.createComponent(component);
            });
    }

    ngAfterViewInit() {
        const video = document.getElementById('hof-video') as HTMLVideoElement;
        document.addEventListener('touchstart', () => {
            video?.play().catch(() => {});
        });

        function playVideo() {
            if (video.paused) {
                video.play().catch((error) => {
                    console.error('Autoplay failed:', error);
                });
            }
        }
        if (!this.domHandler.isDesktop) {
            setTimeout(playVideo, 500);
        }

        const threeLogoCanvas = document.getElementById(
            'logoContainer',
        ) as HTMLDivElement;
        const tl = gsap.timeline();
        tl.to('#first', {
            scrollTrigger: {
                trigger: '#first',
                start: 'top top',
                end: 'bottom top',
                pin: true,
                pinSpacing: false,
            },
        })
            .to('#left-text', {
                xPercent: 130,
                scrollTrigger: {
                    trigger: '#second',
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: true,
                },
                onComplete: () => {
                    if (threeLogoCanvas) {
                        threeLogoCanvas.style.opacity = '0';
                    }
                },
            })
            .from('#right-text', {
                xPercent: 100,
                scrollTrigger: {
                    trigger: '#second',
                    start: 'top center',
                    end: 'bottom top',
                    scrub: true,
                },
                onReverseComplete: () => {
                    if (threeLogoCanvas) {
                        threeLogoCanvas.style.opacity = '1';
                    }
                },
            })
            .to('#third', {
                xPercent: -100,
                scrollTrigger: {
                    trigger: '#third',
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 2,
                    pin: true,
                },
            })
            .to('#fifth', {
                scrollTrigger: {
                    trigger: '#fifth',
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 0.5,
                    pin: true,
                    pinSpacing: false,
                    anticipatePin: 1,
                },
            });

        gsap.from('.niceTo', {
            rotateX: 45,
            marginRight: '200px',
            skewX: 45,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: '#eighth',
                start: 'top bottom',
                end: 'bottom bottom',
                scrub: 1,
            },
        });
        gsap.from('.meatYou', {
            rotateX: 45,
            marginLeft: '200px',
            skewX: 45,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: '#eighth',
                start: 'top bottom',
                end: 'bottom bottom',
                scrub: 1,
            },
        });

        gsap.to('.parallax', {
            yPercent: -35,
            ease: 'none',
            scrollTrigger: {
                trigger: '.parallax',
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            },
        });
        gsap.from('.scale', {
            scale: 1.25,
            scrollTrigger: {
                trigger: '.scale',
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            },
        });

        const phrases = [
            'schmeckt',
            'spürt',
            'lebt',
            'genießt',
            'entdeckt',
            'erlebt',
            'erkennt',
            'schätzt',
            'erfährt',
            'bemerkt',
            'würdigt',
            'feiert',
        ];

        const el = document.getElementById('dynamic-word') as HTMLElement;

        this.typewriterService.startTypewriterEffect(phrases, (text) => {
            el.innerHTML = ` ${text} `;
        });

        setTimeout(() => {
            const introText = document.getElementById(
                'intro-text',
            ) as HTMLElement;
            const split = new SplitType(introText, { types: 'lines' });

            gsap.from(split.lines, {
                opacity: 0.2,
                duration: 0.5,
                ease: 'power1.out',
                stagger: 0.15,
                scrollTrigger: {
                    trigger: introText,
                    start: 'top 90%',
                    end: 'bottom 50%',
                    scrub: true,
                },
            });
        });
    }
}
type HofPage = {
    bestsellers: Bestsellers;
    fullImages: FullImage[];
    textImageOnly: {
        text: BlockNode[];
        image: Image;
    }[];
    categories: Category[];
};
